import { CashRegisterOrder } from "@ac-expo/api-types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { handleApiCall } from "../api-error-handler";
import { privateAxios } from "../axiosInstances";
import { eventsQueryKeys } from "../query-keys";
import cashRegisterQueryKeys from "../query-keys/cash-register.query-keys";
import {
  CashRegisterChangeForm,
  CashRegisterCommandForm,
  CashRegisterStateForm,
  UpsertCashRegisterForm,
} from "../schemas/events/cash-register.schema";

const openCashRegister = async (id: string, body: CashRegisterStateForm) => {
  const { data } = await privateAxios.post(`/cash-register/${id}/open`, body);
  return data;
};

export const useOpenCashRegister = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { id: string; body: CashRegisterStateForm }) =>
      openCashRegister(data.id, data.body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: cashRegisterQueryKeys.me(),
      });
    },
  });
};

const closeCashRegister = async (id: string, body: CashRegisterStateForm) => {
  const { data } = await privateAxios.post(`/cash-register/${id}/close`, body);
  return data;
};

export const useCloseCashRegister = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { id: string; body: CashRegisterStateForm }) =>
      closeCashRegister(data.id, data.body),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: cashRegisterQueryKeys.me(),
      });
    },
  });
};

const registerCashRegisterChange = async (id: string, body: CashRegisterChangeForm) => {
  const { data } = await privateAxios.post(`/cash-register/${id}/change`, body);
  return data;
};

export const useRegisterCashRegisterChange = (id: string) => {
  return useMutation({
    mutationFn: (body: CashRegisterChangeForm) => registerCashRegisterChange(id, body),
  });
};

/**
 * The receipt as the server rendered it, in both forms derived from the same
 * lines: ESC/POS bytes for a network printer, and the 32-column plain text the
 * web POS lays out as is.
 */
export type RenderedReceipt = {
  escposDataBase64: string;
  receiptText: string;
};

export type RegisterCashRegisterCommandResponse = CashRegisterOrder & RenderedReceipt;

const registerCashRegisterCommand = (id: string, body: CashRegisterCommandForm) =>
  handleApiCall(() =>
    privateAxios.post<RegisterCashRegisterCommandResponse>(`/cash-register/${id}/command`, body),
  );

export const useRegisterCashRegisterCommand = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CashRegisterCommandForm) => registerCashRegisterCommand(id, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: cashRegisterQueryKeys.commands(id),
      });
      queryClient.invalidateQueries({
        queryKey: cashRegisterQueryKeys.lastCommand(id),
      });
    },
  });
};

const upsertCashRegister = async (eventId: string, data: UpsertCashRegisterForm) => {
  const { data: response } = await privateAxios.post(`/events/${eventId}/cash-register`, data);
  return response;
};

export const useUpsertCashRegisterMutation = (eventId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpsertCashRegisterForm) => upsertCashRegister(eventId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: eventsQueryKeys.cashRegisters(eventId),
      });
    },
  });
};

const deleteCashRegister = async (eventId: string, cashRegisterId: string) => {
  const { data: response } = await privateAxios.delete(
    `/events/${eventId}/cash-register/${cashRegisterId}`,
  );
  return response;
};

export const useDeleteCashRegisterMutation = (eventId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (cashRegisterId: string) => deleteCashRegister(eventId, cashRegisterId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: eventsQueryKeys.cashRegisters(eventId),
      });
    },
  });
};

type ReprintReceiptResponse = RenderedReceipt & {
  reprintNumber: number;
};

const reprintReceipt = async (
  cashRegisterId: string,
  orderId: string,
): Promise<ReprintReceiptResponse> => {
  const { data } = await privateAxios.post<ReprintReceiptResponse>(
    `/cash-register/${cashRegisterId}/reprint/${orderId}`,
  );
  return data;
};

export const useReprintReceipt = (cashRegisterId: string) => {
  return useMutation({
    mutationFn: (orderId: string) => reprintReceipt(cashRegisterId, orderId),
  });
};

type SendReceiptEmailResponse = {
  success: boolean;
  reprintNumber: number | null;
};

const sendReceiptByEmail = async (
  cashRegisterId: string,
  orderId: string,
  email: string,
): Promise<SendReceiptEmailResponse> => {
  const { data } = await privateAxios.post<SendReceiptEmailResponse>(
    `/cash-register/${cashRegisterId}/send-email/${orderId}`,
    { email },
  );
  return data;
};

export const useSendReceiptByEmail = (cashRegisterId: string) => {
  return useMutation({
    mutationFn: ({ orderId, email }: { orderId: string; email: string }) =>
      sendReceiptByEmail(cashRegisterId, orderId, email),
  });
};
